import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FiDownload, FiBarChart2, FiTrendingUp, FiXCircle, FiCheckCircle, FiClock, FiDollarSign, FiAlertTriangle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const AdminReports = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('Monthly');
  const [reportData, setReportData] = useState([]);
  const [globalStats, setGlobalStats] = useState({});

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (orders.length > 0) {
      processReportData(orders, reportType);
      computeGlobalStats(orders);
    }
  }, [orders, reportType]);

  const fetchOrders = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('orders')
      .select('total_amount, created_at, status, payment_status, shipping_address, promo_discount, order_items(quantity, products(name))')
      .neq('status', 'draft')
      .order('created_at', { ascending: false });

    if (error) {
      console.error("Error fetching orders for report:", error);
    } else {
      setOrders(data || []);
    }
    setLoading(false);
  };

  const computeGlobalStats = (data) => {
    const cancelled = data.filter(o => o.status === 'cancelled');
    const delivered = data.filter(o => o.status === 'delivered');
    const paid = data.filter(o => o.payment_status === 'paid');
    const pending = data.filter(o => o.payment_status !== 'paid' && o.status !== 'cancelled' && o.shipping_address?.paymentMethod !== 'cod');
    const cod = data.filter(o => o.shipping_address?.paymentMethod === 'cod' && o.status !== 'cancelled');

    const totalGross = data.reduce((a, o) => a + o.total_amount, 0);
    const cancelledRevenue = cancelled.reduce((a, o) => a + o.total_amount, 0);
    const totalDiscounts = data.reduce((a, o) => a + (o.promo_discount || 0), 0);
    const netRevenue = totalGross - cancelledRevenue;
    const deliveredRevenue = delivered.reduce((a, o) => a + o.total_amount, 0);
    const paidRevenue = paid.filter(o => o.status !== 'cancelled').reduce((a, o) => a + o.total_amount, 0);
    const pendingRevenue = pending.reduce((a, o) => a + o.total_amount, 0);
    const codRevenue = cod.reduce((a, o) => a + o.total_amount, 0);

    setGlobalStats({
      totalOrders: data.length,
      totalGross,
      cancelledCount: cancelled.length,
      cancelledRevenue,
      deliveredCount: delivered.length,
      deliveredRevenue,
      netRevenue,
      paidCount: paid.length,
      paidRevenue,
      pendingCount: pending.length,
      pendingRevenue,
      codCount: cod.length,
      codRevenue,
      totalDiscounts,
      avgOrderValue: data.length > 0 ? netRevenue / (data.length - cancelled.length || 1) : 0,
    });
  };

  const processReportData = (data, type) => {
    const grouped = {};

    data.forEach(order => {
      const date = new Date(order.created_at);
      let key, sortKey;

      if (type === 'Daily') {
        key = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        sortKey = date.toISOString().split('T')[0];
      } else if (type === 'Weekly') {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        d.setDate(diff);
        key = `Week of ${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
        sortKey = d.toISOString().split('T')[0];
      } else if (type === 'Monthly') {
        key = date.toLocaleString('default', { month: 'long', year: 'numeric' });
        sortKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      } else if (type === 'Yearly') {
        key = `${date.getFullYear()}`;
        sortKey = `${date.getFullYear()}`;
      }

      if (!grouped[key]) {
        grouped[key] = {
          period: key,
          sortKey,
          totalOrders: 0,
          grossRevenue: 0,
          cancelledOrders: 0,
          cancelledAmount: 0,
          paidOrders: 0,
          paidAmount: 0,
          pendingOrders: 0,
          pendingAmount: 0,
          codOrders: 0,
          codAmount: 0,
          deliveredOrders: 0,
          deliveredAmount: 0,
          discounts: 0,
        };
      }

      const g = grouped[key];
      g.totalOrders += 1;
      g.grossRevenue += order.total_amount;
      g.discounts += (order.promo_discount || 0);

      const isCOD = order.shipping_address?.paymentMethod === 'cod';

      if (order.status === 'cancelled') {
        g.cancelledOrders += 1;
        g.cancelledAmount += order.total_amount;
      } else {
        if (order.payment_status === 'paid') {
          g.paidOrders += 1;
          g.paidAmount += order.total_amount;
        } else if (isCOD) {
          g.codOrders += 1;
          g.codAmount += order.total_amount;
        } else {
          g.pendingOrders += 1;
          g.pendingAmount += order.total_amount;
        }
      }

      if (order.status === 'delivered') {
        g.deliveredOrders += 1;
        g.deliveredAmount += order.total_amount;
      }
    });

    const sortedData = Object.values(grouped).sort((a, b) => b.sortKey.localeCompare(a.sortKey));
    setReportData(sortedData);
  };

  const downloadCSV = () => {
    const headers = ['Period', 'Total Orders', 'Gross Revenue', 'Cancelled', 'Cancelled Amount', 'Paid Orders', 'Paid Revenue', 'Pending Orders', 'Pending Revenue', 'COD Orders', 'COD Revenue', 'Net Revenue', 'Discounts Given'];
    const csvRows = reportData.map(row => {
      const net = row.grossRevenue - row.cancelledAmount;
      return `"${row.period}",${row.totalOrders},${row.grossRevenue.toFixed(2)},${row.cancelledOrders},${row.cancelledAmount.toFixed(2)},${row.paidOrders},${row.paidAmount.toFixed(2)},${row.pendingOrders},${row.pendingAmount.toFixed(2)},${row.codOrders},${row.codAmount.toFixed(2)},${net.toFixed(2)},${row.discounts.toFixed(2)}`;
    });
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Ceylanthro_Report_${reportType}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return (
    <div className="p-12 text-center">
      <div className="animate-spin w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full mx-auto mb-4"></div>
      <p className="text-gray-500 text-sm">Loading report data...</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Global Overview Stats */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-6">
          <FiTrendingUp className="text-brand-gold w-5 h-5" />
          <h2 className="text-sm font-bold uppercase tracking-widest text-gray-800">Overall Business Summary</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-green-50 p-4 rounded-xl border border-green-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-green-600 mb-1">Net Revenue</p>
            <p className="text-2xl font-serif text-green-700">Rs. {globalStats.netRevenue?.toFixed(2)}</p>
            <p className="text-[10px] text-green-500 mt-1">Excluding cancelled</p>
          </div>
          <div className="bg-red-50 p-4 rounded-xl border border-red-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-red-600 mb-1">Cancelled</p>
            <p className="text-2xl font-serif text-red-700">Rs. {globalStats.cancelledRevenue?.toFixed(2)}</p>
            <p className="text-[10px] text-red-500 mt-1">{globalStats.cancelledCount || 0} order{globalStats.cancelledCount !== 1 ? 's' : ''}</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600 mb-1">Avg. Order Value</p>
            <p className="text-2xl font-serif text-blue-700">Rs. {globalStats.avgOrderValue?.toFixed(2)}</p>
            <p className="text-[10px] text-blue-500 mt-1">Net avg per order</p>
          </div>
          <div className="bg-amber-50 p-4 rounded-xl border border-amber-100">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600 mb-1">Discounts Given</p>
            <p className="text-2xl font-serif text-amber-700">Rs. {globalStats.totalDiscounts?.toFixed(2)}</p>
            <p className="text-[10px] text-amber-500 mt-1">Total promo savings</p>
          </div>
        </div>

        {/* Payment Breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiCheckCircle} className="text-green-600" size={16} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Paid</p>
              <p className="text-sm font-bold text-gray-800">{globalStats.paidCount || 0} orders</p>
              <p className="text-xs text-green-600 font-semibold">Rs. {globalStats.paidRevenue?.toFixed(2)}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiClock} className="text-yellow-600" size={16} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Pending</p>
              <p className="text-sm font-bold text-gray-800">{globalStats.pendingCount || 0} orders</p>
              <p className="text-xs text-yellow-600 font-semibold">Rs. {globalStats.pendingRevenue?.toFixed(2)}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiDollarSign} className="text-orange-600" size={16} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">COD</p>
              <p className="text-sm font-bold text-gray-800">{globalStats.codCount || 0} orders</p>
              <p className="text-xs text-orange-600 font-semibold">Rs. {globalStats.codRevenue?.toFixed(2)}</p>
            </div>
          </div>
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiXCircle} className="text-red-600" size={16} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Cancelled</p>
              <p className="text-sm font-bold text-gray-800">{globalStats.cancelledCount || 0} orders</p>
              <p className="text-xs text-red-600 font-semibold">Rs. {globalStats.cancelledRevenue?.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Table Section */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-gray-100 pb-6 mb-6">
          <div className="flex items-center gap-3">
            <FiBarChart2 className="text-brand-gold w-6 h-6" />
            <div>
              <h2 className="text-xl font-serif text-gray-800">Period Breakdown</h2>
              <p className="text-xs text-gray-400 mt-0.5">Net revenue excludes cancelled orders</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-gray-50 rounded-lg p-1 border border-gray-200">
              {['Daily', 'Weekly', 'Monthly', 'Yearly'].map(type => (
                <button
                  key={type}
                  onClick={() => setReportType(type)}
                  className={`px-4 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all ${
                    reportType === type 
                      ? 'bg-white text-brand-darkSage shadow-sm' 
                      : 'text-gray-500 hover:text-brand-darkSage'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
            <button 
              onClick={downloadCSV}
              className="flex items-center gap-2 bg-brand-darkSage text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-brand-gold transition-colors"
            >
              <FiDownload size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Period Totals */}
        {reportData.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-brand-sand/30 p-4 rounded-xl border border-brand-darkSage/10">
              <p className="text-[10px] font-bold text-brand-lightText uppercase tracking-widest mb-1">Total Net Revenue</p>
              <p className="text-2xl font-serif text-brand-darkSage">Rs. {reportData.reduce((a, r) => a + (r.grossRevenue - r.cancelledAmount), 0).toFixed(2)}</p>
            </div>
            <div className="bg-brand-sand/30 p-4 rounded-xl border border-brand-darkSage/10">
              <p className="text-[10px] font-bold text-brand-lightText uppercase tracking-widest mb-1">Total Orders</p>
              <p className="text-2xl font-serif text-brand-darkSage">{reportData.reduce((a, r) => a + r.totalOrders, 0)}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-xl border border-green-100">
              <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest mb-1">Delivered Revenue</p>
              <p className="text-2xl font-serif text-green-700">Rs. {reportData.reduce((a, r) => a + r.deliveredAmount, 0).toFixed(2)}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-xl border border-red-100">
              <p className="text-[10px] font-bold text-red-600 uppercase tracking-widest mb-1">Lost to Cancellations</p>
              <p className="text-2xl font-serif text-red-700">Rs. {reportData.reduce((a, r) => a + r.cancelledAmount, 0).toFixed(2)}</p>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/80 text-gray-400 text-[10px] uppercase tracking-[0.15em]">
                <th className="p-4 font-bold rounded-tl-lg">Period</th>
                <th className="p-4 font-bold text-center">Orders</th>
                <th className="p-4 font-bold text-right">Gross</th>
                <th className="p-4 font-bold text-center">
                  <span className="text-red-400">Cancelled</span>
                </th>
                <th className="p-4 font-bold text-center">
                  <span className="text-green-500">Paid</span>
                </th>
                <th className="p-4 font-bold text-center">
                  <span className="text-yellow-500">Pending</span>
                </th>
                <th className="p-4 font-bold text-center">
                  <span className="text-orange-500">COD</span>
                </th>
                <th className="p-4 font-bold text-right rounded-tr-lg">Net Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {reportData.map((row, index) => {
                const netRevenue = row.grossRevenue - row.cancelledAmount;
                return (
                  <tr key={index} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-4 text-sm font-medium text-gray-800">{row.period}</td>
                    <td className="p-4 text-center">
                      <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md font-mono text-xs">
                        {row.totalOrders}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-600 text-right font-mono">
                      {row.grossRevenue.toFixed(2)}
                    </td>
                    <td className="p-4 text-center">
                      {row.cancelledOrders > 0 ? (
                        <div className="inline-flex flex-col items-center">
                          <span className="text-xs font-bold text-red-500">{row.cancelledOrders}</span>
                          <span className="text-[10px] text-red-400">-{row.cancelledAmount.toFixed(0)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">0</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {row.paidOrders > 0 ? (
                        <div className="inline-flex flex-col items-center">
                          <span className="text-xs font-bold text-green-600">{row.paidOrders}</span>
                          <span className="text-[10px] text-green-500">{row.paidAmount.toFixed(0)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">0</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {row.pendingOrders > 0 ? (
                        <div className="inline-flex flex-col items-center">
                          <span className="text-xs font-bold text-yellow-600">{row.pendingOrders}</span>
                          <span className="text-[10px] text-yellow-500">{row.pendingAmount.toFixed(0)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">0</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {row.codOrders > 0 ? (
                        <div className="inline-flex flex-col items-center">
                          <span className="text-xs font-bold text-orange-600">{row.codOrders}</span>
                          <span className="text-[10px] text-orange-500">{row.codAmount.toFixed(0)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">0</span>
                      )}
                    </td>
                    <td className="p-4 text-sm font-bold text-brand-darkSage text-right font-mono">
                      {netRevenue.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
              {reportData.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-12 text-center text-gray-400 text-sm">
                    <FiAlertTriangle className="w-8 h-8 mx-auto mb-3 text-gray-300" />
                    No data available for the selected period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminReports;